/* The one fetch core, always bound to a door.
 *
 * The product is a single origin in front of the platform's FastAPI doors, each reachable
 * at `/d/<doorId>` (the vite proxy in dev, the Cilium Gateway route in production — both
 * rendered from the same `sx_service.registry` rows). So an endpoint is a one-liner off a *bound* client:
 * `const catalog = doorClient("catalog")`, then `catalog<Dataset[]>("/datasets")` hits
 * `/d/catalog/api/datasets`. Errors are `ApiError` carrying the HTTP status with detail
 * extracted from FastAPI's `{ detail }` body; the session is a cookie, so every request
 * uses `credentials: "include"`.
 *
 * There used to be an unbound `request` hard-coding `/api`, because each SPA owned an
 * origin and the door was implied by which app you were in. On one origin the door is a
 * real parameter, and a call site that could omit it is a call site that could reach the
 * wrong backend.
 *
 * The door *table* is not this package's to know — it is generated from the registry into
 * the consumer — so `doorClients` takes it and hands back the vocabulary bound to it,
 * keyed by the consumer's own door-id union. This package owns how a door is spoken to;
 * the registry owns which doors exist.
 *
 * `detail` arrives in three shapes: a plain string, a typed { code, message } envelope
 * (sandbox/quota errors that need a machine-readable code), or pydantic's validation
 * array. All three narrow to message + optional code. */

import { DecodeError, type Decoder } from "./decode";

/* How long a call waits for a door before the wait itself is the failure.
 *
 * A request with no deadline is not patient, it is silent: fetch will hold a connection
 * a middlebox has already dropped until the browser gives up minutes later, and until
 * then the console shows a skeleton with nothing behind it. So both numbers below are
 * about a *person*, not about a door:
 *
 *   A read is something someone is looking at. Twenty seconds is already far past the
 *   point where they have concluded it is broken; past it, saying so and repairing in
 *   the background beats waiting.
 *
 *   A write is something someone did, and abandoning it is not free — the door may have
 *   already applied it. Sixty seconds, because the honest failure for a write is late,
 *   and because a long upload or a synchronous door-side commit is legitimate.
 *
 * A timeout surfaces as a 504: the one recovery vocabulary already reads that as
 * transient, so the read repairs itself and a region keeps its snapshot. Streams and
 * SSE carry no deadline at all — a stream that is quiet is doing its job. */
export const READ_TIMEOUT_MS = 20_000;
export const WRITE_TIMEOUT_MS = 60_000;

/** The HTTP methods with no request body and no effect. Everything else is a write. */
const READ_METHODS: ReadonlySet<string> = new Set(["GET", "HEAD", "OPTIONS"]);

export const timeoutFor = (method: string | undefined): number =>
  READ_METHODS.has((method ?? "GET").toUpperCase())
    ? READ_TIMEOUT_MS
    : WRITE_TIMEOUT_MS;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code: string | null = null,
    /** The door the request addressed, when the boundary that threw knew it. The failure
     * record keys on it; a message that merely mentions the door is not a field. */
    public door: string | undefined = undefined,
  ) {
    super(message);
  }
}

/** A door that ran out of time, in the one vocabulary the recovery policy reads. */
export function timedOut(
  door: string,
  path: string,
  timeoutMs: number,
): ApiError {
  return new ApiError(
    504,
    `${door} did not answer ${path} within ${Math.round(timeoutMs / 1_000)}s`,
    "timeout",
    door,
  );
}

/** An abort, however the runtime spells it: a `DOMException` in a browser, a plain
 * `Error` named the same in some fetch implementations. */
const isAbort = (cause: unknown): boolean =>
  typeof cause === "object" &&
  cause !== null &&
  (cause as { name?: unknown }).name === "AbortError";

interface Deadline {
  readonly signal: AbortSignal;
  /** True only when *this* deadline fired. A caller's own abort — a component that
   * unmounted, a superseded search — must stay an abort and never be reported as a
   * door failing. */
  readonly expired: boolean;
  done(): void;
}

/** One signal that is aborted by whichever comes first: the deadline, or the caller.
 *
 * `AbortSignal.any` would say this in one line and is not available everywhere this
 * bundle runs; `AbortSignal.timeout` alone cannot be combined with the caller's. */
function deadlineFor(
  timeoutMs: number,
  callerSignal: AbortSignal | null | undefined,
): Deadline {
  const controller = new AbortController();
  const state = { expired: false };
  const timer = setTimeout(() => {
    state.expired = true;
    controller.abort();
  }, timeoutMs);
  const relay = () => controller.abort(callerSignal?.reason);
  if (callerSignal?.aborted) relay();
  else callerSignal?.addEventListener("abort", relay, { once: true });
  return {
    signal: controller.signal,
    get expired() {
      return state.expired;
    },
    done: () => {
      clearTimeout(timer);
      callerSignal?.removeEventListener("abort", relay);
    },
  };
}

function errorFromDetail(
  door: string,
  status: number,
  statusText: string,
  detail: unknown,
): ApiError {
  if (typeof detail === "string" && detail) {
    return new ApiError(status, detail, null, door);
  }
  if (typeof detail === "object" && detail !== null) {
    const envelope = detail as { code?: unknown; message?: unknown };
    if (typeof envelope.message === "string") {
      const code = typeof envelope.code === "string" ? envelope.code : null;
      return new ApiError(status, envelope.message, code, door);
    }
    // Pydantic's validation array (or any other shape) — keep it readable
    // rather than dropping the only description of what went wrong.
    return new ApiError(status, JSON.stringify(detail), null, door);
  }
  // Absent, empty, or non-descriptive detail: the status line is the message.
  return new ApiError(
    status,
    statusText || `Request failed (${status})`,
    null,
    door,
  );
}

function detailFromPayload(payload: unknown): unknown {
  if (typeof payload !== "object" || payload === null) return undefined;
  if ("detail" in payload) return (payload as { detail: unknown }).detail;
  if (
    "message" in payload &&
    typeof (payload as { message: unknown }).message === "string"
  ) {
    return payload;
  }
  return undefined;
}

function decodeResponse<T>(
  door: string,
  path: string,
  raw: unknown,
  decode?: Decoder<T>,
  source?: string,
): T {
  if (decode === undefined) return raw as T;
  try {
    return decode(raw, source);
  } catch (cause) {
    if (cause instanceof DecodeError) {
      throw new ApiError(
        502,
        `${door} returned an unexpected response for ${path}: ${cause.message}`,
        "response_shape",
        door,
      );
    }
    throw cause;
  }
}

/** A fetch bound to one door. `path` is the door-relative API path (`/datasets`), never a
 * URL — the console prefix and the `/api` root belong to the platform, not the call site.
 *
 * `decode` parses the response body instead of asserting it. Without one the body is cast
 * to `T`, which is a promise the wire never made — see `decode.ts`. A read whose failure
 * would take down more than its own screen should pass one. */
export type DoorClient = <T>(
  path: string,
  init?: RequestInit,
  decode?: Decoder<T>,
) => Promise<T>;

export interface UploadProgress {
  readonly loadedBytes: number;
  readonly totalBytes: number;
}

export interface UploadInit {
  readonly body: XMLHttpRequestBodyInit;
  readonly method?: "POST" | "PUT" | "PATCH";
  readonly headers?: HeadersInit;
  readonly signal?: AbortSignal;
  readonly onProgress?: (progress: UploadProgress) => void;
}

/** The useful response facts from a direct object-store upload. The browser only exposes
 * CORS-approved headers; multipart callers rely on the owner configuring `ETag` there. */
export interface ExternalUploadReceipt {
  readonly status: number;
  readonly headers: Headers;
}

/** XHR is the browser's upload transport because Fetch does not expose request-body
 * progress. It otherwise follows the same door, cookie, Project, JSON, and error contract
 * as DoorClient. */
export type DoorUploadClient = <T>(
  path: string,
  init: UploadInit,
  decode?: Decoder<T>,
) => Promise<T>;

function uploadRequest(
  url: string,
  init: UploadInit,
  withCredentials: boolean,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    if (init.signal?.aborted) {
      reject(new DOMException("Upload aborted.", "AbortError"));
      return;
    }
    const request = new XMLHttpRequest();
    const headers = new Headers(init.headers);
    let settled = false;

    const finish = (result: () => void) => {
      if (settled) return;
      settled = true;
      init.signal?.removeEventListener("abort", abort);
      result();
    };
    const abort = () => request.abort();

    request.open(init.method ?? "POST", url);
    request.withCredentials = withCredentials;
    headers.forEach((value, name) => request.setRequestHeader(name, value));
    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && event.total > 0) {
        init.onProgress?.({
          loadedBytes: Math.min(event.loaded, event.total),
          totalBytes: event.total,
        });
      }
    });
    request.addEventListener("error", () =>
      finish(() => reject(new ApiError(0, "Network request failed."))),
    );
    request.addEventListener("abort", () =>
      finish(() => reject(new DOMException("Upload aborted.", "AbortError"))),
    );
    request.addEventListener("load", () => finish(() => resolve(request)));

    init.signal?.addEventListener("abort", abort, { once: true });
    request.send(init.body);
  });
}

function responseHeaders(request: XMLHttpRequest): Headers {
  const headers = new Headers();
  for (const line of request
    .getAllResponseHeaders()
    .trim()
    .split(/[\r\n]+/)) {
    if (!line) continue;
    const separator = line.indexOf(":");
    if (separator <= 0) continue;
    headers.append(
      line.slice(0, separator).trim(),
      line.slice(separator + 1).trim(),
    );
  }
  return headers;
}

/** Upload a raw body to an owner-issued, cross-origin object-store URL without sending
 * Console cookies. `XMLHttpRequest.withCredentials = false` still sends same-origin
 * cookies, so accepting a Console-origin URL here would make the no-cookie contract false.
 * Control-plane calls stay on a bound door; this is only the byte leg after that door has
 * authorized a short-lived destination. */
export async function uploadToUrl(
  url: string,
  init: UploadInit,
): Promise<ExternalUploadReceipt> {
  const destination = new URL(url);
  if (destination.protocol !== "https:" && destination.protocol !== "http:") {
    throw new TypeError("Upload URL must use HTTP or HTTPS.");
  }
  if (
    typeof globalThis.location !== "undefined" &&
    globalThis.location.origin !== "null" &&
    destination.origin === globalThis.location.origin
  ) {
    throw new TypeError("Upload URL must use a separate object-store origin.");
  }
  const request = await uploadRequest(destination.href, init, false);
  if (request.status < 200 || request.status >= 300) {
    throw new ApiError(
      request.status,
      request.statusText || `Upload failed (${request.status})`,
    );
  }
  return { status: request.status, headers: responseHeaders(request) };
}

export interface DoorVocabulary<Id extends string> {
  /** Bind the fetch core to one door. */
  doorClient: (door: Id) => DoorClient;
  /** Bind the fetch core to one door and one Project.
   *
   * Project-scoped guards reject an ambiguous request. Keeping that invariant in the
   * transport boundary means a workspace cannot accidentally lose the scope while
   * merging another header (or by supplying a legal `Headers` instance). */
  projectDoorClient: (door: Id, projectId: string) => DoorClient;
  /** Bind a progress-reporting upload to one door. */
  doorUpload: (door: Id) => DoorUploadClient;
  /** Bind a progress-reporting upload to one door and one Project. */
  projectDoorUpload: (door: Id, projectId: string) => DoorUploadClient;
  /** The console path a door answers on (`/d/catalog`).
   *
   * `doorClient` is how a *fetch* reaches a door and is what a call site should normally
   * want. This is for the addresses a fetch cannot carry: an `EventSource`, an ai-sdk chat
   * transport that builds its own request, the connectivity probe, and the SDK base URL
   * handed to a customer to paste. Rebuilding `/d/<door>` by hand would be a second copy
   * of a generated row. */
  doorPrefix: (door: Id) => string;
}

/** Bind the fetch vocabulary to a generated door → console-prefix table. */
export function doorClients<Id extends string>(
  prefixes: Readonly<Record<Id, string>>,
): DoorVocabulary<Id> {
  const doorPrefix = (door: Id): string => prefixes[door];

  const bindDoor = (door: Id, projectId?: string): DoorClient => {
    const base = `${doorPrefix(door)}/api`;
    return async function call<T>(
      path: string,
      init?: RequestInit,
      decode?: Decoder<T>,
    ): Promise<T> {
      const headers = new Headers(init?.headers);
      if (!headers.has("Content-Type"))
        headers.set("Content-Type", "application/json");
      if (projectId !== undefined) headers.set("X-SX-Project-Id", projectId);
      // The deadline covers the body too: a door that answers 200 and then stalls
      // mid-stream is the same silence to the person waiting for it.
      const deadline = deadlineFor(timeoutFor(init?.method), init?.signal);
      try {
        const resp = await fetch(`${base}${path}`, {
          ...init,
          credentials: "include",
          cache: init?.cache ?? "no-store",
          headers,
          signal: deadline.signal,
        });
        if (!resp.ok) {
          let detail: unknown;
          try {
            detail = detailFromPayload(await resp.json());
          } catch {
            /* non-JSON error body */
          }
          throw errorFromDetail(door, resp.status, resp.statusText, detail);
        }
        if (resp.status === 204) return undefined as T;
        const contentType = resp.headers.get("content-type") ?? "";
        if (!contentType.toLowerCase().includes("application/json")) {
          throw new ApiError(
            502,
            `${door} returned ${contentType || "an unknown content type"} for ${path}`,
            null,
            door,
          );
        }
        const source = await resp.text();
        let raw: unknown;
        try {
          raw = JSON.parse(source);
        } catch {
          throw new ApiError(
            502,
            `${door} returned invalid JSON for ${path}`,
            null,
            door,
          );
        }
        return decodeResponse(door, path, raw, decode, source);
      } catch (cause) {
        // Both conditions, and the second is the one that matters: the deadline covers
        // the body too, so a real 404 or 502 whose body read straddles the timer would
        // otherwise be relabelled a timeout and become retryable. Only the abort this
        // deadline itself caused is a timeout; an `ApiError` is the door's own answer,
        // and a caller's abort stays an abort.
        throw deadline.expired && isAbort(cause)
          ? timedOut(door, path, timeoutFor(init?.method))
          : cause;
      } finally {
        deadline.done();
      }
    };
  };

  const bindUpload = (door: Id, projectId?: string): DoorUploadClient => {
    const base = `${doorPrefix(door)}/api`;
    return async function upload<T>(
      path: string,
      init: UploadInit,
      decode?: Decoder<T>,
    ): Promise<T> {
      const headers = new Headers(init.headers);
      if (projectId !== undefined) {
        headers.set("X-SX-Project-Id", projectId);
      }
      const request = await uploadRequest(
        `${base}${path}`,
        { ...init, headers },
        true,
      );
      let raw: unknown;
      let validJson = true;
      try {
        raw = request.responseText
          ? JSON.parse(request.responseText)
          : undefined;
      } catch {
        validJson = false;
      }
      if (request.status < 200 || request.status >= 300) {
        throw errorFromDetail(
          door,
          request.status,
          request.statusText,
          detailFromPayload(raw),
        );
      }
      if (request.status === 204) return undefined as T;
      const contentType = request.getResponseHeader("content-type") ?? "";
      if (!contentType.toLowerCase().includes("application/json")) {
        throw new ApiError(
          502,
          `${door} returned ${contentType || "an unknown content type"} for ${path}`,
          null,
          door,
        );
      }
      if (!validJson) {
        throw new ApiError(
          502,
          `${door} returned invalid JSON for ${path}`,
          null,
          door,
        );
      }
      return decodeResponse(door, path, raw, decode, request.responseText);
    };
  };

  const doorClient = (door: Id): DoorClient => bindDoor(door);
  const projectDoorClient = (door: Id, projectId: string): DoorClient =>
    bindDoor(door, projectId);
  const doorUpload = (door: Id): DoorUploadClient => bindUpload(door);
  const projectDoorUpload = (door: Id, projectId: string): DoorUploadClient =>
    bindUpload(door, projectId);

  return {
    doorClient,
    projectDoorClient,
    doorUpload,
    projectDoorUpload,
    doorPrefix,
  };
}

export const body = (data: unknown): RequestInit => ({
  body: JSON.stringify(data),
});
